<script lang="ts">
  /**
   * Popover з деревом полів JSON-payload. Клік на лист → виклик `onPick`
   * з JSONPath ("$.path.to.field"). Працює рекурсивно для object/array,
   * для array показуємо тільки `[0]` (типово достатньо для inspection).
   */
  import Icon from '../Icon.svelte';
  let {
    payload,
    onPick,
    onClose,
    inline = false,
  }: {
    payload: unknown;
    onPick: (path: string) => void;
    onClose: () => void;
    inline?: boolean;
  } = $props();

  // Розгортання вузлів — за замовчуванням всі object/array на верхньому
  // рівні розгорнуті.
  let expanded = $state<Record<string, boolean>>({ $: true });

  function toggle(path: string) {
    expanded[path] = !expanded[path];
  }

  function typeLabel(v: unknown): string {
    if (v === null) return 'null';
    if (Array.isArray(v)) return `array(${v.length})`;
    if (typeof v === 'object') return `object`;
    return typeof v;
  }

  function preview(v: unknown): string {
    if (v === null) return 'null';
    if (v === undefined) return 'undefined';
    if (typeof v === 'string') return `"${v.length > 30 ? v.slice(0, 30) + '…' : v}"`;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    return '';
  }

  interface Row {
    path: string; // "$.a.b[0]"
    label: string; // "a" or "[0]"
    depth: number;
    value: unknown;
    kind: 'leaf' | 'object' | 'array';
  }

  function buildRows(value: unknown, path = '$', depth = 0): Row[] {
    const rows: Row[] = [];
    if (value === null || value === undefined) {
      return rows;
    }
    if (Array.isArray(value)) {
      // показуємо тільки перший елемент — він репрезентує shape
      if (value.length > 0) {
        const childPath = `${path}[0]`;
        const child = value[0];
        const isContainer = child !== null && typeof child === 'object';
        rows.push({
          path: childPath,
          label: '[0]',
          depth,
          value: child,
          kind: isContainer ? (Array.isArray(child) ? 'array' : 'object') : 'leaf',
        });
        if (isContainer && expanded[childPath]) {
          rows.push(...buildRows(child, childPath, depth + 1));
        }
      }
      return rows;
    }
    if (typeof value === 'object') {
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        const childPath = `${path}.${k}`;
        const isContainer = v !== null && typeof v === 'object';
        rows.push({
          path: childPath,
          label: k,
          depth,
          value: v,
          kind: isContainer ? (Array.isArray(v) ? 'array' : 'object') : 'leaf',
        });
        if (isContainer && expanded[childPath]) {
          rows.push(...buildRows(v, childPath, depth + 1));
        }
      }
    }
    return rows;
  }

  const rows = $derived(buildRows(payload));

  function pickField(row: Row) {
    onPick(row.path);
  }
</script>

<div
  class="{inline ? 'relative bg-white rounded-xl border border-[var(--color-border-3)] max-h-[400px] overflow-y-auto' : 'absolute z-50 bg-white rounded-xl border border-[var(--color-border-3)] shadow-2xl w-[320px] max-h-[400px] overflow-y-auto'}"
  onmouseleave={inline ? undefined : onClose}
  role="dialog"
>
  <div class="sticky top-0 bg-[var(--color-bg-3)] px-3 py-2 border-b border-[var(--color-border-3)] flex items-center justify-between">
    <div class="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] font-bold">
      <Icon name="listView" size={12} /> Поля з тестового payload
    </div>
    <button onclick={onClose} class="inline-flex items-center text-[var(--color-text-muted)] hover:text-rose-600"><Icon name="close" size={13} /></button>
  </div>

  {#if !payload || (typeof payload === 'object' && payload !== null && Object.keys(payload as object).length === 0)}
    <div class="p-3 text-xs italic text-[var(--color-text-muted)]">
      Спершу виконайте тестовий запит зверху, щоб побачити поля.
    </div>
  {:else if rows.length === 0}
    <div class="p-3 text-xs italic text-[var(--color-text-muted)]">payload порожній</div>
  {:else}
    <div class="py-1">
      {#each rows as r (r.path)}
        <div class="flex items-center gap-1 px-2 py-1 hover:bg-[var(--color-bg-3)] cursor-pointer" style="padding-left: {8 + r.depth * 14}px">
          {#if r.kind !== 'leaf'}
            <button
              onclick={(e) => {
                e.stopPropagation();
                toggle(r.path);
              }}
              class="inline-flex w-4 items-center text-[var(--color-text-muted)]"
            >{#if expanded[r.path]}<Icon name="chevronDown" size={12} />{:else}<Icon name="chevronRight" size={12} />{/if}</button>
          {:else}
            <span class="w-4"></span>
          {/if}
          <button
            class="flex-1 text-left flex items-center gap-2 text-xs"
            onclick={() => pickField(r)}
            title="Вставити {r.path}"
          >
            <span class="font-mono font-semibold text-[var(--color-pu-3)]">{r.label}</span>
            <span class="text-[10px] text-[var(--color-text-muted)]">{typeLabel(r.value)}</span>
            <span class="ml-auto text-[10px] text-[var(--color-text-muted)] truncate max-w-[120px]">{preview(r.value)}</span>
          </button>
        </div>
      {/each}
    </div>
  {/if}
</div>
